import { Box } from "@mui/material";
import { ReactNode, isValidElement } from "react";

type IconEnhanceProps = {
  size?: number;
  color?: string;
  /**
   * Stroke weight for the glyph, in the icon's own 24-unit viewBox.
   *
   * Lucide draws at 2, which at a 16px render is 1.33 device pixels — thin
   * enough that a column of them reads as grey rather than as icons. Set in
   * CSS rather than as a prop so it reaches an animated glyph's nested paths
   * too, which take their own attributes.
   */
  strokeWidth?: number;
};

/**
 * Sizes and colours a caller-supplied icon node for the slot it is being
 * rendered into (a breadcrumb, a menu row, a table cell action).
 *
 * It does this with CSS rather than by cloning props into the element. The
 * previous version did the latter, and it broke the moment icons stopped
 * taking a numeric `size`: it cast the child to `ReactElement<{size?: number}>`
 * — a cast, so the compiler could not object — and injected `size={24}` into
 * `<Icon>`, whose size is a token. `ICON_SIZES[24]` is undefined, so every icon
 * routed through here silently fell back to lucide's own 24px default. That is
 * why breadcrumbs, menu rows and header buttons all rendered oversized while
 * the sidebar items still on raw lucide looked right.
 *
 * CSS wins over the width/height attributes both `Icon` and raw lucide render,
 * so this works for either without knowing which it got — which is the whole
 * point of a slot that takes a ReactNode.
 *
 * Callers that own their icon outright should skip this and pass a size token
 * to `Icon` directly; this exists for slots whose icon arrives as a prop.
 */
export const applyIconProps = (
  icon: ReactNode,
  { color, size, strokeWidth }: IconEnhanceProps
): ReactNode => {
  if (!isValidElement(icon)) return icon;

  return (
    <Box
      component="span"
      sx={{
        ...(color && { color }),
        ...((size !== undefined || strokeWidth !== undefined) && {
          "& svg": {
            ...(size !== undefined && { height: size, width: size }),
            ...(strokeWidth !== undefined && { strokeWidth })
          }
        }),
        alignItems: "center",
        display: "inline-flex"
      }}
    >
      {icon}
    </Box>
  );
};
